import { PartnerKeyType } from 'interfaces/IIntegration'

/** A sync partner the mapping can target. */
export type Partner = {
  key: PartnerKeyType
  name?: string
}

/**
 * Flow of a (DIRECT) mapping, inferred from the record:
 *  - `outbound` (Adopets → External): we start from our side and pick the
 *    external counterpart. Default for new mappings and fully-set records.
 *  - `inbound` (External → Adopets): an external value arrived unmapped
 *    (external_id set, our side still null), so we start from the external info
 *    and pick our side.
 */
export type Flow = 'outbound' | 'inbound'

/**
 * Scope of the mapping:
 *  - `global`: applies to all shelters (no `shelter_uuid`).
 *  - `shelter`: a per-shelter override (`shelter_uuid` required).
 */
export type ScopeMode = 'global' | 'shelter'

/**
 * Mapping type (Color / Branch):
 *  - `DIRECT`: a fixed pair — our value ↔ the external value (auto-detected `flow`).
 *  - `MATCH`: a one-way rule whose source side is decided by a JS `expression`.
 */
export type TypeKey = 'DIRECT' | 'MATCH'

/**
 * Direction of a MATCH rule:
 *  - `INPUT` (external → adopets): the expression reads the incoming external pet;
 *    the result is our value.
 *  - `OUTPUT` (adopets → external): the expression reads our pet; the result is the
 *    external value (name + external id).
 *  - `BOTH` (adopets ↔ external): a DIRECT mapping syncs both ways; this is the
 *    internal direction the backend stores for DIRECT. The MATCH radios never
 *    offer it — it's implied by the type.
 */
export type DirectionKey = 'INPUT' | 'OUTPUT' | 'BOTH'

export const DIRECTION_LABEL: Record<DirectionKey, string> = {
  INPUT: 'Input (external → adopets)',
  OUTPUT: 'Output (adopets → external)',
  BOTH: 'Adopets ↔ External',
}
